import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import ModalStandard from "../../../common/components/Modal/ModalStandard";
import { Error } from "../../../common/components/AppState/Error";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { InputIcon } from "../../../common/components/Input";
import { setAlert } from "../../alert/state/alertsSlice";
import {
  clearEnrollmentWizardState,
  disableMfa,
  fetchMfaStatus,
  getMfaStatus,
  regenerateRecoveryCodes,
  sendMfaStepUpCode,
} from "../state/mfaSlice";
import MfaEnrollDialog from "./MfaEnrollDialog";
import RecoveryCodesDialog from "./RecoveryCodesDialog";

const MFA_DISABLE_COMPONENT_ID = "mfaDisableComponent";
const MFA_REGENERATE_COMPONENT_ID = "mfaRegenerateComponent";
const MFA_STEP_UP_COMPONENT_ID = "mfaSectionStepUpComponent";
const CODE_PATTERN = /^\d{6}$/;
const RECOVERY_CODES_LOW_THRESHOLD = 3;
const STEP_UP_COOLDOWN_SECONDS = 30;

type CredentialConfirmAction = "disable" | "regenerate";

interface MfaSectionProps {
  componentId: string;
}

/**
 * Profile-page MFA enrollment/management block
 * (docs/plans/todo/creator-mfa-two-factor-auth.md, Phase 6 step 3). Mounted
 * in ProfileMain.tsx near the existing password-change controls.
 *
 * Layout/classes reuse the row wrapper from ProfileInputStyle.tsx and the
 * btn-primary-fill/btn-alternative-outline buttons + InputIcon password field
 * from ChangePasswordPopup.tsx — no new UI pattern introduced.
 */
const MfaSection = ({ componentId }: MfaSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const status = useAppSelector(getMfaStatus);

  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [isReplacementFlow, setIsReplacementFlow] = useState(false);
  const [recoveryCodesDialogOpen, setRecoveryCodesDialogOpen] = useState(false);

  // Shared password+code confirmation, gating disable and regenerate alike —
  // Appendix D requires both. Mirrors ChangePasswordPopup's
  // confirm-then-act shape rather than inventing a new one.
  const [credentialConfirmAction, setCredentialConfirmAction] =
    useState<CredentialConfirmAction | null>(null);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [confirmCode, setConfirmCode] = useState("");

  // "an email factor is not a one-way door" addendum (B3) - see the identical
  // block in MfaEnrollDialog.tsx for why this is component-local state, not
  // Redux: it's an ephemeral, single-purpose challenge token, not wizard
  // material that needs to survive a re-render of anything else.
  const [stepUpToken, setStepUpToken] = useState<string | null>(null);
  const [stepUpSent, setStepUpSent] = useState(false);
  const [stepUpSending, setStepUpSending] = useState(false);
  const [stepUpCooldown, setStepUpCooldown] = useState(0);

  useEffect(() => {
    void dispatch(fetchMfaStatus({ componentId }));
  }, []);

  useEffect(() => {
    if (stepUpCooldown <= 0) {
      return undefined;
    }
    const timer = setInterval(() => setStepUpCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => clearInterval(timer);
  }, [stepUpCooldown]);

  const openActivate = () => {
    setIsReplacementFlow(false);
    setEnrollDialogOpen(true);
  };

  const openChangeMethod = () => {
    setIsReplacementFlow(true);
    setEnrollDialogOpen(true);
  };

  const closeEnrollDialog = () => {
    dispatch(clearEnrollmentWizardState());
    setEnrollDialogOpen(false);
  };

  const handleEnrolled = () => {
    setEnrollDialogOpen(false);
    setRecoveryCodesDialogOpen(true);
    void dispatch(fetchMfaStatus({ componentId }));
    // P3: disableMfa already confirms with a toast (below); activation and
    // method-replacement were the two silent completions left.
    dispatch(
      setAlert({
        type: "success",
        msg: t(
          isReplacementFlow
            ? "alerts.mfaMethodChangedSuccessfully"
            : "alerts.mfaEnabledSuccessfully",
        ),
      }),
    );
  };

  const closeRecoveryCodesDialog = () => {
    dispatch(clearEnrollmentWizardState());
    setRecoveryCodesDialogOpen(false);
  };

  const openCredentialConfirm = (action: CredentialConfirmAction) => {
    setConfirmPassword("");
    setConfirmCode("");
    setStepUpToken(null);
    setStepUpSent(false);
    setStepUpSending(false);
    setStepUpCooldown(0);
    setCredentialConfirmAction(action);
  };

  const closeCredentialConfirm = () => {
    setCredentialConfirmAction(null);
    setConfirmPassword("");
    setConfirmCode("");
    setStepUpToken(null);
    setStepUpSent(false);
    setStepUpSending(false);
    setStepUpCooldown(0);
  };

  // "an email factor is not a one-way door" (B3): an email-method user has no
  // way to prove their CURRENT factor here beyond a recovery code, since
  // there's no in-flight challenge to check a typed code against outside a
  // login. This mints one on demand (mfaStepUpSend.js) and emails the OTP -
  // the resulting token is paired with that OTP below instead of a recovery
  // code. Recovery code stays accepted throughout - this is an addition, not
  // a replacement (see handleCredentialConfirmSubmit's codeArgs).
  const handleSendStepUp = () => {
    if (stepUpCooldown > 0 || stepUpSending) {
      return;
    }
    setStepUpSending(true);
    setStepUpSent(false);
    void dispatch(sendMfaStepUpCode({ componentId: MFA_STEP_UP_COMPONENT_ID }))
      .unwrap()
      .then((res) => {
        setStepUpToken(res.step_up_token);
        setStepUpSent(true);
        setStepUpSending(false);
        setStepUpCooldown(STEP_UP_COOLDOWN_SECONDS);
      })
      .catch(() => {
        // [fix 2026-08-07] A FAILED send must not look like a successful one.
        // Previously this started the cooldown and left the "code sent" state
        // alone, so a 429 (the limit is per user, per 15 minutes) rendered as a
        // countdown with no error — the user then waited for an email that was
        // never sent, typed nothing, and got a flat rejection. Found in a
        // browser, not in review.
        //
        // No cooldown on failure: nothing was sent, so there is nothing to
        // throttle, and blocking the button would strand them further. The
        // error surfaces through the shared <Error componentId={...}> slot.
        setStepUpSending(false);
        setStepUpSent(false);
      });
  };

  const handleCredentialConfirmSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Accepts either a current 6-digit code or a recovery code — both are
    // valid per mfaDisable.js/mfaRegenerateRecoveryCodes.js. A bare 6-digit
    // entry only proves an existing TOTP credential though (verified
    // directly against its secret) — an email credential has nothing to
    // check it against unless it's paired with a step-up token (B3), so a
    // recovery code stays the only accepted proof for email until one has
    // been requested via "Email me a code" below.
    const trimmedCode = confirmCode.trim();
    const isSixDigitCode = CODE_PATTERN.test(trimmedCode);
    const isEmailMethod = status?.method === "email";
    const canUseStepUp = isEmailMethod && Boolean(stepUpToken) && isSixDigitCode;
    const codeArgs =
      isEmailMethod && !canUseStepUp
        ? { recoveryCode: trimmedCode }
        : canUseStepUp
        ? { otp: trimmedCode, stepUpToken: stepUpToken as string }
        : isSixDigitCode
        ? { otp: trimmedCode }
        : { recoveryCode: trimmedCode };

    if (credentialConfirmAction === "disable") {
      void dispatch(
        disableMfa({
          componentId: MFA_DISABLE_COMPONENT_ID,
          password: confirmPassword,
          ...codeArgs,
        }),
      ).then((res) => {
        if (disableMfa.fulfilled.match(res)) {
          closeCredentialConfirm();
          dispatch(setAlert({ type: "success", msg: t("alerts.mfaDisabledSuccessfully") }));
        }
      });
    } else if (credentialConfirmAction === "regenerate") {
      void dispatch(
        regenerateRecoveryCodes({
          componentId: MFA_REGENERATE_COMPONENT_ID,
          password: confirmPassword,
          ...codeArgs,
        }),
      ).then((res) => {
        if (regenerateRecoveryCodes.fulfilled.match(res)) {
          closeCredentialConfirm();
          setRecoveryCodesDialogOpen(true);
          // P3: disableMfa already confirms with a toast - regeneration was
          // silent beyond the (unmissable, but separate) codes dialog itself.
          dispatch(
            setAlert({ type: "success", msg: t("alerts.mfaRecoveryCodesRegeneratedSuccessfully") }),
          );
        }
      });
    }
  };

  const rowClassName =
    "w-full flex flex-col sm:flex-row items-center py-2 px-2 xl:py-3 xl:px-4 border-t border-gray-300";

  const statusLabel = !status
    ? ""
    : status.enabled && status.method === "totp"
    ? t("pages.profile.mfa.statusEnabledTotp")
    : status.enabled && status.method === "email"
    ? t("pages.profile.mfa.statusEnabledEmail")
    : t("pages.profile.mfa.statusDisabled");

  return (
    <>
      <h2 className="text-2xl font-medium mb-3 mt-8">{t("pages.profile.mfa.sectionTitle")}</h2>
      <p className="text-sm text-gray-500 mb-2">{t("pages.profile.mfa.sectionDescription")}</p>

      <div className="completely-centeredProfile">
        <IsLoading componentId={componentId} showSuccess={false} />
      </div>

      <div className={rowClassName}>
        <p className="w-full sm:w-1/3 text-sm text-center sm:text-left">
          {t("pages.profile.mfa.statusLabel")}
        </p>
        <p className="flex-1 text-sm font-medium text-center sm:text-left">{statusLabel}</p>
        <div className="flex grow justify-end gap-2">
          {!status?.enabled && (
            <button type="button" className="btn-primary-fill" onClick={openActivate}>
              {t("pages.profile.mfa.activate")}
            </button>
          )}
          {status?.enabled && (
            <>
              <button type="button" className="btn-alternative-outline" onClick={openChangeMethod}>
                {t("pages.profile.mfa.changeMethod")}
              </button>
              {/* D5: this turns 2FA off immediately - it must not look like
                  the harmless "Change method or device" button above.
                  Precedent: .btn-danger-fill (src/index.css), used the same
                  way by ModalConfirmDangerousAction.tsx. */}
              <button
                type="button"
                className="btn-danger-fill"
                disabled={status.mandatory}
                onClick={() => openCredentialConfirm("disable")}
              >
                {t("pages.profile.mfa.deactivate")}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mandatory-org-admin hint. Always rendered (invisible when not
          applicable) so its row never shifts layout when it appears —
          precedent: SelectPlatformsStep.tsx's time-estimate line. */}
      <p
        className={`w-full text-xs text-gray-500 px-2 xl:px-4 pt-1 ${
          status?.enabled && status.mandatory ? "" : "invisible"
        }`}
      >
        {t("pages.profile.mfa.mandatoryHint")}
      </p>

      {status?.enabled && (
        <div className={rowClassName}>
          <p className="w-full sm:w-1/3 text-sm text-center sm:text-left">
            {t("pages.profile.mfa.recoveryCodesRemainingLabel")}
          </p>
          <p className="flex-1 text-sm font-medium text-center sm:text-left">
            {status.recoveryCodesRemaining ?? "-"}
          </p>
          <div className="flex grow justify-end">
            {/* D5: regenerating invalidates every existing code immediately -
                same danger language as Deactivate above. */}
            <button
              type="button"
              className="btn-danger-fill"
              onClick={() => openCredentialConfirm("regenerate")}
            >
              {t("pages.profile.mfa.regenerateRecoveryCodes")}
            </button>
          </div>
        </div>
      )}

      {/* P2: reserved space (invisible when not applicable) so the row never
          shifts when the warning appears — precedent: RateLimitLine.tsx's
          threshold-based warning line, and the mandatoryHint row above. */}
      <p
        className={`w-full text-xs text-amber-600 px-2 xl:px-4 pt-1 ${
          status?.enabled &&
          status.recoveryCodesRemaining !== null &&
          status.recoveryCodesRemaining !== undefined &&
          status.recoveryCodesRemaining <= RECOVERY_CODES_LOW_THRESHOLD
            ? ""
            : "invisible"
        }`}
      >
        {t("pages.profile.mfa.recoveryCodesLowWarning")}
      </p>

      <MfaEnrollDialog
        isOpen={enrollDialogOpen}
        isReplacement={isReplacementFlow}
        // Drives what the replacement step is allowed to ask for: an email
        // factor can only be confirmed with a recovery code.
        currentMethod={status?.method ?? null}
        onClose={closeEnrollDialog}
        onEnrolled={handleEnrolled}
      />

      <RecoveryCodesDialog isOpen={recoveryCodesDialogOpen} onClose={closeRecoveryCodesDialog} />

      <ModalStandard
        id="modalMfaCredentialConfirm"
        fullScreen={false}
        controlledMode={{ isOpen: credentialConfirmAction !== null }}
        onClose={closeCredentialConfirm}
      >
        <div className="flex w-full">
          <div className="completely-centered z-50">
            <IsLoading componentId={MFA_DISABLE_COMPONENT_ID} showSuccess={false} />
            <IsLoading componentId={MFA_REGENERATE_COMPONENT_ID} showSuccess={false} />
            <IsLoading componentId={MFA_STEP_UP_COMPONENT_ID} showSuccess={false} />
          </div>
          <div className="relative grow w-full max-w-7xl px-4 xl:px-0 mx-auto">
            <h2 className="pt-3 pb-1 font-medium text-lg">
              {credentialConfirmAction === "disable"
                ? t("pages.profile.mfa.confirmDisableTitle")
                : t("pages.profile.mfa.confirmRegenerateTitle")}
            </h2>
            <h3 className="pb-3">{t("pages.profile.mfa.confirmSubtitle")}</h3>
            {/* D5: irreversible actions must look irreversible - same red
                banner language as ModalConfirmDangerousAction.tsx's
                warningMessages (bg-red-100/text-red-700), used elsewhere by
                ProjectsList.tsx and ReportsListView.tsx. The type-the-name
                ceremony that component also has isn't needed here - a
                password + a code/recovery code is already the confirmation. */}
            <div className="bg-red-100 rounded-lg p-3 text-red-700 font-medium text-sm mb-3">
              {credentialConfirmAction === "disable"
                ? t("pages.profile.mfa.confirmDisableWarning")
                : t("pages.profile.mfa.confirmRegenerateWarning")}
            </div>
            <form onSubmit={handleCredentialConfirmSubmit}>
              <div className="mb-2">
                <InputIcon
                  autoFocus
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setConfirmPassword(event.target.value)
                  }
                  type={confirmPasswordVisible ? "text" : "password"}
                  position="right"
                  placeholder={t("pages.profile.main.changePassword.oldPasswordPlaceholder")}
                  handleClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                  icon={confirmPasswordVisible ? "EyeIcon" : "EyeOffIcon"}
                />
              </div>
              <InputIcon
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setConfirmCode(event.target.value)
                }
                type="text"
                position="right"
                icon="KeyIcon"
                placeholder={
                  // B2: method-aware, exactly like MfaEnrollDialog's
                  // replacement step - an EMAIL credential has no in-flight
                  // challenge to check a typed 6-digit code against outside a
                  // login, so asking for "current code or recovery code"
                  // regardless of method is an instruction an email-factor
                  // user cannot follow (mfa_disable_invalid). B3: once a
                  // step-up code has been emailed below, a 6-digit entry is
                  // accepted again too.
                  status?.method === "email" && !stepUpToken
                    ? t("pages.profile.mfa.enroll.recoveryCodeOnlyPlaceholder")
                    : t("pages.profile.mfa.enroll.currentCodePlaceholder")
                }
              />
              {status?.method === "email" && (
                <div className="flex justify-between items-center mt-1">
                  <button
                    type="button"
                    className="text-purple-500 font-medium text-xs border-0 disabled:text-gray-400"
                    onClick={handleSendStepUp}
                    disabled={stepUpCooldown > 0 || stepUpSending}
                  >
                    {stepUpCooldown > 0
                      ? t("pages.login.mfaChallenge.resendCooldown", { seconds: stepUpCooldown })
                      : t("pages.profile.mfa.enroll.emailStepUpButton")}
                  </button>
                </div>
              )}
              <p
                className={`text-xs text-green-600 mt-1 ${
                  stepUpSent && status?.method === "email" ? "" : "invisible"
                }`}
              >
                {t("pages.profile.mfa.enroll.emailStepUpSent")}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {status?.method === "email"
                  ? t("pages.profile.mfa.enroll.replaceHintEmail")
                  : t("pages.profile.mfa.enroll.replaceHint")}
              </p>

              <Error
                componentId={MFA_STEP_UP_COMPONENT_ID}
                errorString={t("pages.profile.mfa.enroll.stepUpSendFailed")}
              />
              <Error componentId={MFA_DISABLE_COMPONENT_ID} />
              <Error componentId={MFA_REGENERATE_COMPONENT_ID} />

              <div className="flex justify-end mt-3">
                {/* D5: this is the actual irreversible act (turn off 2FA /
                    invalidate every existing recovery code) - it must not be
                    the same friendly green as every other confirm button in
                    the app. */}
                <button
                  className="btn-danger-fill"
                  type="submit"
                  disabled={!confirmPassword || !confirmCode.trim()}
                >
                  {t("general.confirm")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </ModalStandard>
    </>
  );
};

export default MfaSection;
